import { createFileRoute, Link } from "@tanstack/react-router";
import * as React from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { requireAuth } from "~/lib/auth-guard";
import { orpc } from "~/lib/orpc-client";
import { Skeleton, useToast } from "@alchema/ui";
import * as m from "~/paraglide/messages";
import { RouteErrorBoundary } from "~/components/RouteErrorBoundary";
import { ViewSwitcher } from "~/components/shared/ViewSwitcher";
import { TableIcon, KanbanIcon } from "~/components/shared/ViewIcons";
import { EmptyState } from "~/components/shared/EmptyState";

export const Route = createFileRoute("/applications")({
  beforeLoad: async () => { await requireAuth(); },
  component: ApplicationsPage,
  pendingComponent: ApplicationsSkeleton,
  errorComponent: RouteErrorBoundary,
});

function ApplicationsSkeleton() {
  return (
    <div className="animate-fade-up">
      <div className="page-header">
        <Skeleton className="h-8 w-40" />
      </div>
      <div className="mt-6 flex gap-3 overflow-x-auto">
        {[1, 2, 3, 4, 5].map((i) => (
          <Skeleton key={i} className="h-64 min-w-[200px] flex-1 rounded-xl" />
        ))}
      </div>
    </div>
  );
}

const STATUS_PIPELINE = [
  { value: "applied", label: () => m.applications_status_applied(), color: "bg-[var(--accent-subtle)] text-[var(--accent-text)]" },
  { value: "screening", label: () => m.applications_status_screening(), color: "bg-[var(--warning-subtle)] text-[var(--warning)]" },
  { value: "interview", label: () => m.applications_status_interview(), color: "bg-status-purple-bg text-status-purple" },
  { value: "offer", label: () => m.applications_status_offer(), color: "bg-[var(--success-subtle)] text-[var(--success)]" },
  { value: "rejected", label: () => m.applications_status_rejected(), color: "bg-[var(--error-subtle)] text-[var(--error)]" },
  { value: "withdrawn", label: () => m.applications_status_withdrawn(), color: "bg-[var(--bg-secondary)] text-[var(--text-secondary)]" },
];

const KANBAN_COLUMNS = [
  { status: "applied", label: "Applied", color: "var(--accent)" },
  { status: "screening", label: "Screening", color: "var(--warning)" },
  { status: "interview", label: "Interview", color: "var(--info)" },
  { status: "offer", label: "Offer", color: "var(--success)" },
  { status: "rejected", label: "Rejected", color: "var(--error)" },
] as const;

const INITIAL_COLORS = ["#e76f51", "#6366f1", "#10b981", "#f59e0b", "#3b82f6", "#8b5cf6"];

function getInitialBg(name: string): string {
  const hash = name.split("").reduce((acc, c) => acc + c.charCodeAt(0), 0);
  return INITIAL_COLORS[hash % INITIAL_COLORS.length];
}

type StatusValue = "applied" | "screening" | "interview" | "offer" | "rejected" | "withdrawn";

function ApplicationsPage() {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const appsQuery = useQuery(orpc.application.list.queryOptions({ input: undefined }));
  const [editingNotesId, setEditingNotesId] = React.useState<string | null>(null);
  const [notesDraft, setNotesDraft] = React.useState("");
  const [expandedNotesId, setExpandedNotesId] = React.useState<string | null>(null);
  const [view, setView] = React.useState<"kanban" | "table">("kanban");

  const updateStatusMutation = useMutation(orpc.application.updateStatus.mutationOptions({
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: orpc.application.list.key({}) });
      toast(m.applications_status_updated(), "success");
    },
  }));

  const updateNotesMutation = useMutation(orpc.application.updateNotes.mutationOptions({
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: orpc.application.list.key({}) });
      setEditingNotesId(null);
      toast(m.applications_notes_saved(), "success");
    },
    onError: () => toast(m.applications_notes_failed(), "error"),
  }));

  function startEditNotes(app: { id: string; notes: string | null }) {
    setEditingNotesId(app.id);
    setNotesDraft(app.notes ?? "");
  }

  function saveNotes(id: string) {
    updateNotesMutation.mutate({ id, notes: notesDraft.trim() || null });
  }

  const applications = appsQuery.data ?? [];

  return (
    <div className="animate-fade-up">
      {/* Editorial page header */}
      <div className="flex items-end justify-between gap-4 pb-6">
        <div>
          <h1
            className="text-2xl font-semibold tracking-tight"
            style={{ color: "var(--text)" }}
          >
            {m.applications_title()}
          </h1>
          <p
            className="mt-1 text-sm"
            style={{ color: "var(--text-muted)" }}
          >
            {m.applications_tracked_count({ count: applications.length, plural: applications.length !== 1 ? "s" : "" })}
          </p>
        </div>
        <ViewSwitcher
          views={[
            { id: "kanban", label: "Kanban", icon: KanbanIcon },
            { id: "table", label: "Table", icon: TableIcon },
          ]}
          active={view}
          onChange={(v) => setView(v as "kanban" | "table")}
        />
      </div>

      {appsQuery.isLoading ? (
        <ApplicationsSkeleton />
      ) : applications.length === 0 ? (
        <EmptyState
          icon={
            <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5}>
              <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
              <polyline points="22 4 12 14.01 9 11.01" />
            </svg>
          }
          title="No applications tracked yet"
          description="Start applying to jobs to see your pipeline here"
          action={{ label: "Find Jobs", href: "/jobs" }}
        />
      ) : view === "kanban" ? (
        <KanbanView
          applications={applications}
          updateStatusMutation={updateStatusMutation}
          editingNotesId={editingNotesId}
          notesDraft={notesDraft}
          setNotesDraft={setNotesDraft}
          expandedNotesId={expandedNotesId}
          setExpandedNotesId={setExpandedNotesId}
          startEditNotes={startEditNotes}
          saveNotes={saveNotes}
          updateNotesMutation={updateNotesMutation}
          setEditingNotesId={setEditingNotesId}
        />
      ) : (
        <TableView
          applications={applications}
          updateStatusMutation={updateStatusMutation}
          editingNotesId={editingNotesId}
          notesDraft={notesDraft}
          setNotesDraft={setNotesDraft}
          expandedNotesId={expandedNotesId}
          setExpandedNotesId={setExpandedNotesId}
          startEditNotes={startEditNotes}
          saveNotes={saveNotes}
          updateNotesMutation={updateNotesMutation}
          setEditingNotesId={setEditingNotesId}
        />
      )}
    </div>
  );
}

/* ---------- Shared types for view props ---------- */

interface ApplicationItem {
  id: string;
  jobId: string;
  jobTitle?: string | undefined;
  jobCompany?: string | undefined;
  jobLocation?: string | undefined;
  status: string | null;
  method: string | null;
  notes: string | null;
  appliedAt: string;
}

interface ViewProps {
  applications: ApplicationItem[];
  updateStatusMutation: ReturnType<typeof useMutation<unknown, Error, { id: string; status: StatusValue }>>;
  editingNotesId: string | null;
  notesDraft: string;
  setNotesDraft: (v: string) => void;
  expandedNotesId: string | null;
  setExpandedNotesId: (v: string | null) => void;
  startEditNotes: (app: { id: string; notes: string | null }) => void;
  saveNotes: (id: string) => void;
  updateNotesMutation: ReturnType<typeof useMutation<unknown, Error, { id: string; notes: string | null }>>;
  setEditingNotesId: (v: string | null) => void;
}

/* ---------- Kanban View ---------- */

function KanbanView({
  applications,
  updateStatusMutation,
  editingNotesId,
  notesDraft,
  setNotesDraft,
  expandedNotesId,
  setExpandedNotesId,
  startEditNotes,
  saveNotes,
  updateNotesMutation,
  setEditingNotesId,
}: ViewProps) {
  return (
    <div className="flex gap-3 overflow-x-auto pb-4" style={{ minHeight: 300 }}>
      {KANBAN_COLUMNS.map((col) => {
        const items = applications.filter((a) => (a.status || "applied") === col.status);
        return (
          <div
            key={col.status}
            className="flex min-w-[220px] flex-1 flex-col"
            style={{ maxWidth: 340 }}
          >
            {/* Column header */}
            <div className="mb-3 flex items-center gap-2 px-1">
              <span
                className="inline-block h-2 w-2 rounded-full"
                style={{ background: col.color }}
              />
              <span
                className="text-xs font-semibold"
                style={{ color: "var(--text-secondary)", letterSpacing: "0.01em" }}
              >
                {col.label}
              </span>
              <span
                className="text-xs tabular-nums"
                style={{ color: "var(--text-muted)" }}
              >
                {items.length}
              </span>
            </div>

            {/* Column body */}
            <div
              className="flex flex-1 flex-col gap-2 rounded-lg p-2"
              style={{
                background: "var(--bg-secondary)",
                border: items.length === 0 ? "1px dashed var(--border-subtle)" : "none",
                minHeight: 120,
              }}
            >
              {items.length === 0 ? (
                <div className="flex flex-1 items-center justify-center">
                  <span className="text-xs" style={{ color: "var(--text-muted)" }}>
                    No items
                  </span>
                </div>
              ) : (
                items.map((app) => (
                  <KanbanCard
                    key={app.id}
                    app={app}
                    updateStatusMutation={updateStatusMutation}
                    isEditing={editingNotesId === app.id}
                    isExpanded={expandedNotesId === app.id}
                    notesDraft={notesDraft}
                    setNotesDraft={setNotesDraft}
                    setExpandedNotesId={setExpandedNotesId}
                    startEditNotes={startEditNotes}
                    saveNotes={saveNotes}
                    updateNotesMutation={updateNotesMutation}
                    setEditingNotesId={setEditingNotesId}
                  />
                ))
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}

/* ---------- Kanban Card ---------- */

interface KanbanCardProps {
  app: ApplicationItem;
  updateStatusMutation: ViewProps["updateStatusMutation"];
  isEditing: boolean;
  isExpanded: boolean;
  notesDraft: string;
  setNotesDraft: (v: string) => void;
  setExpandedNotesId: (v: string | null) => void;
  startEditNotes: (app: { id: string; notes: string | null }) => void;
  saveNotes: (id: string) => void;
  updateNotesMutation: ViewProps["updateNotesMutation"];
  setEditingNotesId: (v: string | null) => void;
}

function KanbanCard({
  app,
  updateStatusMutation,
  isEditing,
  isExpanded,
  notesDraft,
  setNotesDraft,
  setExpandedNotesId,
  startEditNotes,
  saveNotes,
  updateNotesMutation,
  setEditingNotesId,
}: KanbanCardProps) {
  const company = app.jobCompany || m.applications_unknown_company();
  const initial = company.charAt(0).toUpperCase();

  return (
    <div
      className="rounded-lg p-3"
      style={{
        background: "var(--surface)",
        border: "1px solid var(--border-subtle)",
      }}
    >
      {/* Title + company */}
      <div className="flex items-start gap-2.5">
        <div
          className="flex h-7 w-7 shrink-0 items-center justify-center rounded-md text-[11px] font-bold text-white"
          style={{ background: getInitialBg(company) }}
        >
          {initial}
        </div>
        <div className="min-w-0 flex-1">
          <Link
            to="/jobs/$jobId"
            params={{ jobId: app.jobId }}
            className="block truncate font-medium hover:underline"
            style={{ fontSize: 13, color: "var(--text)" }}
          >
            {app.jobTitle || m.applications_untitled_job()}
          </Link>
          <p
            className="mt-0.5 truncate"
            style={{ fontSize: 11, color: "var(--text-muted)" }}
          >
            {company}
            {app.jobLocation ? ` \u00B7 ${app.jobLocation}` : ""}
          </p>
        </div>
      </div>

      {/* Applied date */}
      <p className="mt-2" style={{ fontSize: 10, color: "var(--text-muted)" }}>
        {m.applications_applied_date({ date: new Date(app.appliedAt).toLocaleDateString() })}
      </p>

      {/* Status selector */}
      <select
        value={app.status || "applied"}
        onChange={(e) =>
          updateStatusMutation.mutate({
            id: app.id,
            status: e.target.value as StatusValue,
          })
        }
        className="input mt-2 w-full"
        style={{ fontSize: 11, padding: "3px 6px" }}
      >
        {STATUS_PIPELINE.map((s) => (
          <option key={s.value} value={s.value}>
            {s.label()}
          </option>
        ))}
      </select>

      {/* Notes (compact) */}
      {isEditing ? (
        <div className="mt-2 space-y-1.5">
          <textarea
            value={notesDraft}
            onChange={(e) => setNotesDraft(e.target.value)}
            placeholder={m.applications_notes_placeholder()}
            rows={2}
            className="input w-full"
            style={{ fontSize: 11 }}
          />
          <div className="flex gap-1.5">
            <button
              onClick={() => saveNotes(app.id)}
              disabled={updateNotesMutation.isPending}
              className="btn btn-primary btn-sm"
              style={{ fontSize: 10, padding: "2px 8px" }}
            >
              {updateNotesMutation.isPending ? m.common_saving() : m.common_save()}
            </button>
            <button
              onClick={() => setEditingNotesId(null)}
              className="btn btn-ghost btn-sm"
              style={{ fontSize: 10, padding: "2px 8px" }}
            >
              {m.common_cancel()}
            </button>
          </div>
        </div>
      ) : (
        <button
          onClick={() => {
            if (app.notes) {
              setExpandedNotesId(isExpanded ? null : app.id);
            } else {
              startEditNotes(app);
            }
          }}
          onDoubleClick={() => startEditNotes(app)}
          className="mt-2 block w-full text-left transition-colors"
          style={{ fontSize: 10, color: "var(--text-muted)" }}
        >
          {app.notes ? (
            <span className={isExpanded ? "" : "line-clamp-2"}>{app.notes}</span>
          ) : (
            <span className="italic opacity-60">{m.applications_add_notes()}</span>
          )}
        </button>
      )}
    </div>
  );
}

/* ---------- Table View ---------- */

function TableView({
  applications,
  updateStatusMutation,
  editingNotesId,
  notesDraft,
  setNotesDraft,
  expandedNotesId,
  setExpandedNotesId,
  startEditNotes,
  saveNotes,
  updateNotesMutation,
  setEditingNotesId,
}: ViewProps) {
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-left" style={{ borderCollapse: "separate", borderSpacing: 0 }}>
        <thead>
          <tr>
            {["Job", "Status", "Method", "Applied", "Notes", ""].map((h) => (
              <th
                key={h}
                className="whitespace-nowrap px-3 py-2 text-xs font-medium"
                style={{
                  color: "var(--text-muted)",
                  borderBottom: "1px solid var(--border-subtle)",
                }}
              >
                {h}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {applications.map((app) => {
            const company = app.jobCompany || m.applications_unknown_company();
            const initial = company.charAt(0).toUpperCase();
            const statusEntry = STATUS_PIPELINE.find((s) => s.value === (app.status || "applied"));
            const colDef = KANBAN_COLUMNS.find((c) => c.status === (app.status || "applied"));
            const isEditing = editingNotesId === app.id;
            const isExpanded = expandedNotesId === app.id;

            return (
              <tr
                key={app.id}
                className="transition-colors"
                style={{ borderBottom: "1px solid var(--border-subtle)" }}
              >
                {/* Job Title + Company */}
                <td className="px-3 py-3">
                  <div className="flex items-center gap-2.5">
                    <div
                      className="flex h-7 w-7 shrink-0 items-center justify-center rounded-md text-[11px] font-bold text-white"
                      style={{ background: getInitialBg(company) }}
                    >
                      {initial}
                    </div>
                    <div className="min-w-0">
                      <Link
                        to="/jobs/$jobId"
                        params={{ jobId: app.jobId }}
                        className="block truncate text-sm font-medium hover:underline"
                        style={{ color: "var(--text)" }}
                      >
                        {app.jobTitle || m.applications_untitled_job()}
                      </Link>
                      <p className="truncate" style={{ fontSize: 11, color: "var(--text-muted)" }}>
                        {company}
                        {app.jobLocation ? ` \u00B7 ${app.jobLocation}` : ""}
                      </p>
                    </div>
                  </div>
                </td>

                {/* Status */}
                <td className="px-3 py-3">
                  <div className="flex items-center gap-1.5">
                    <select
                      value={app.status || "applied"}
                      onChange={(e) =>
                        updateStatusMutation.mutate({
                          id: app.id,
                          status: e.target.value as StatusValue,
                        })
                      }
                      className="input"
                      style={{ fontSize: 11, padding: "2px 6px", minWidth: 100 }}
                    >
                      {STATUS_PIPELINE.map((s) => (
                        <option key={s.value} value={s.value}>
                          {s.label()}
                        </option>
                      ))}
                    </select>
                    {colDef && (
                      <span
                        className="inline-block h-2 w-2 shrink-0 rounded-full"
                        style={{ background: colDef.color }}
                      />
                    )}
                  </div>
                </td>

                {/* Method */}
                <td className="px-3 py-3">
                  <span className="text-xs" style={{ color: "var(--text-secondary)" }}>
                    {app.method || "\u2014"}
                  </span>
                </td>

                {/* Applied date */}
                <td className="whitespace-nowrap px-3 py-3">
                  <span className="text-xs" style={{ color: "var(--text-muted)" }}>
                    {new Date(app.appliedAt).toLocaleDateString()}
                  </span>
                </td>

                {/* Notes */}
                <td className="px-3 py-3" style={{ maxWidth: 200 }}>
                  {isEditing ? (
                    <div className="space-y-1.5">
                      <textarea
                        value={notesDraft}
                        onChange={(e) => setNotesDraft(e.target.value)}
                        placeholder={m.applications_notes_placeholder()}
                        rows={2}
                        className="input w-full"
                        style={{ fontSize: 11 }}
                      />
                      <div className="flex gap-1.5">
                        <button
                          onClick={() => saveNotes(app.id)}
                          disabled={updateNotesMutation.isPending}
                          className="btn btn-primary btn-sm"
                          style={{ fontSize: 10, padding: "2px 8px" }}
                        >
                          {updateNotesMutation.isPending ? m.common_saving() : m.common_save()}
                        </button>
                        <button
                          onClick={() => setEditingNotesId(null)}
                          className="btn btn-ghost btn-sm"
                          style={{ fontSize: 10, padding: "2px 8px" }}
                        >
                          {m.common_cancel()}
                        </button>
                      </div>
                    </div>
                  ) : (
                    <button
                      onClick={() => {
                        if (app.notes) {
                          setExpandedNotesId(isExpanded ? null : app.id);
                        } else {
                          startEditNotes(app);
                        }
                      }}
                      onDoubleClick={() => startEditNotes(app)}
                      className="block w-full text-left transition-colors"
                      style={{ fontSize: 11, color: "var(--text-muted)" }}
                    >
                      {app.notes ? (
                        <span className={isExpanded ? "" : "line-clamp-1"}>{app.notes}</span>
                      ) : (
                        <span className="italic opacity-60">{m.applications_add_notes()}</span>
                      )}
                    </button>
                  )}
                </td>

                {/* Actions (placeholder) */}
                <td className="px-3 py-3">
                  <Link
                    to="/jobs/$jobId"
                    params={{ jobId: app.jobId }}
                    className="text-xs font-medium transition-colors"
                    style={{ color: "var(--accent)" }}
                  >
                    View
                  </Link>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
